<template>
  <svg
    v-if="partes"
    v-bind="$attrs"
    class="contagem-live"
    viewBox="0 0 392 222"
    preserveAspectRatio="xMidYMid slice"
    :width="largura"
    :height="altura"
    role="img"
    :aria-label="rotuloAcessivel"
  >
    <defs>
      <linearGradient :id="idFundo" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="#000000" />
        <stop offset="0.126" stop-color="#000000" />
        <stop offset="0.505" stop-color="#5a5757" />
        <stop offset="0.9" stop-color="#1d1d1d" />
        <stop offset="1" stop-color="#1d1d1d" />
      </linearGradient>
      <linearGradient :id="idCartao" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="#dad9d9" />
        <stop offset="0.5" stop-color="#dedede" />
        <stop offset="1" stop-color="#dbdada" />
      </linearGradient>
    </defs>
    <rect width="392" height="222" :fill="`url(#${idFundo})`" />
    <image v-if="capa && !capaFalhou" :href="capa" width="392" height="222" preserveAspectRatio="xMidYMid slice" />
    <g v-for="grupo in grupos" :key="grupo.chave">
      <template v-for="(digito, i) in grupo.digitos" :key="i">
        <rect :x="grupo.x + i * 30" y="90" width="27" height="46" rx="4" :fill="`url(#${idCartao})`" />
        <text :x="grupo.x + i * 30 + 13.5" y="121.8" text-anchor="middle" class="contagem-live__digito">{{ digito }}</text>
      </template>
      <text :x="grupo.x + 28.5" y="151.5" text-anchor="middle" class="contagem-live__rotulo">{{ grupo.rotulo }}</text>
    </g>
    <rect v-for="ponto in PONTOS" :key="ponto.join('-')" :x="ponto[0]" :y="ponto[1]" width="3" height="3" fill="#ffffff" />
  </svg>
  <slot v-else />
</template>

<script lang="ts" setup>
/**
 * Contagem regressiva para o início da live, no lugar da capa do post do blog nos
 * grids da home e do blog. Segue o modelo de 392x222: cartões cinza com dígitos
 * vermelhos, dois-pontos brancos e os rótulos embaixo de cada par. As medidas saíram
 * do próprio modelo.
 *
 * O fundo é a própria capa do post (`capa`), recortada como a imagem do card. O
 * degradê do modelo fica por baixo: só aparece enquanto a capa carrega, sem capa ou
 * com a capa que não carrega.
 *
 * Recebe a largura e a altura da capa que substitui e preenche a mesma caixa, como um
 * object-fit: cover, então o grid não muda de tamanho. O componente pai estiliza o
 * `.contagem-live` como estiliza a imagem.
 *
 * Sem `inicio`, depois do início ou com 100 dias ou mais, mostra o slot (a capa). No
 * SSR e na hidratação usa o "agora" do servidor, para o HTML bater; montado, segue o
 * relógio do navegador e troca para a capa sozinho no início.
 */
import { computed, onBeforeUnmount, onMounted, ref, useId, watch } from "vue";
import translaters from "@/tradutors/blogs";
import { doisDigitos, partesDaContagem } from "@/utils/contagemRegressiva";

defineOptions({ inheritAttrs: false });

const props = defineProps<{
  inicio?: string | null;
  capa?: string | null;
  largura?: number | string;
  altura?: number | string;
}>();

// Os dois-pontos: dois quadrados de 3px no meio do vão entre os pares. Os dias
// terminam em 121 e as horas começam em 165 (meio em 143); as horas terminam em 222
// e os minutos começam em 268 (meio em 245). O x é o do canto do quadrado.
const PONTOS = [
  [141.5, 107],
  [141.5, 116],
  [243.5, 107],
  [243.5, 116],
];

const route = useRoute();
const nuxtApp = useNuxtApp();
const translate = computed(() =>
  route.params.language === "pt-br" ? translaters.portugues : translaters.ingles
);

const id = useId();
const idFundo = `contagem-fundo-${id}`;
const idCartao = `contagem-cartao-${id}`;

const agoraDoServidor = useState<number>("contagem-live-agora", () => Date.now());
const agora = ref<number>(nuxtApp.isHydrating ? agoraDoServidor.value : Date.now());
const partes = computed(() => partesDaContagem(props.inicio, agora.value));

const grupos = computed(() => {
  if (!partes.value) {
    return [];
  }

  return [
    { chave: "dias", x: 64, digitos: doisDigitos(partes.value.dias), rotulo: translate.value.contagemDias },
    { chave: "horas", x: 165, digitos: doisDigitos(partes.value.horas), rotulo: translate.value.contagemHoras },
    { chave: "minutos", x: 268, digitos: doisDigitos(partes.value.minutos), rotulo: translate.value.contagemMinutos },
  ];
});

const rotuloAcessivel = computed(() => {
  if (!partes.value) {
    return "";
  }

  return translate.value.contagemRotulo
    .replace("{dias}", String(partes.value.dias))
    .replace("{horas}", String(partes.value.horas))
    .replace("{minutos}", String(partes.value.minutos));
});

let relogio: ReturnType<typeof setInterval> | null = null;

function paraORelogio() {
  if (relogio) {
    clearInterval(relogio);
    relogio = null;
  }
}

// Só há relógio enquanto houver contagem: os demais posts do grid não gastam nada.
function ligaORelogio() {
  paraORelogio();
  agora.value = Date.now();
  if (!partes.value) {
    return;
  }

  relogio = setInterval(() => {
    agora.value = Date.now();
    if (!partes.value) {
      paraORelogio();
    }
  }, 1000);
}

// Capa que não carrega (arquivo que não existe, rede): o Chrome desenharia o ícone de
// imagem quebrada esticado sobre a contagem. A capa sai e fica o degradê. A conferência
// é no navegador, porque no SSR o erro pode acontecer antes da hidratação.
const capaFalhou = ref(false);

function confereACapa() {
  capaFalhou.value = false;
  if (!props.capa || !partes.value) {
    return;
  }

  const imagem = new Image();
  imagem.onerror = () => {
    capaFalhou.value = true;
  };
  imagem.src = props.capa;
}

onMounted(() => {
  ligaORelogio();
  confereACapa();
});
watch(() => [props.inicio, props.capa], () => {
  if (import.meta.client) {
    ligaORelogio();
    confereACapa();
  }
});
onBeforeUnmount(paraORelogio);
</script>

<style>
/* Fontes da contagem, do fontsource, sem alteração (licença OFL): os dígitos do
   modelo são da Source Sans 3, e os rótulos, da Exo 2 SemiBold. O navegador só as
   baixa quando há contagem na tela. */
@font-face {
  font-family: "Contagem-Digitos";
  src: url("../../assets/fonts/SourceSans3-Regular.woff2") format("woff2");
  font-display: swap;
}

@font-face {
  font-family: "Contagem-Rotulos";
  src: url("../../assets/fonts/Exo2-SemiBold.woff2") format("woff2");
  font-display: swap;
}
</style>

<style scoped>
.contagem-live__digito {
  font-family: "Contagem-Digitos", "Exo-2", sans-serif;
  font-size: 28.8px;
  fill: #b30008;
}

.contagem-live__rotulo {
  font-family: "Contagem-Rotulos", "Exo-2", sans-serif;
  font-size: 11px;
  fill: #ffffff;
}
</style>
